import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import './QuestionBlock.css'

const PLACEHOLDER_QUESTION_COUNT = 3

const QuestionBlock = ({
  items = [],
  isLoading = false,
  title = 'Ответы на вопросы',
  description = 'Мы позаботились о вас и собрали часто\nзадаваемые вопросы',
  ariaLabel = 'Ответы на вопросы',
  className = '',
}) => {
  const [openedIndex, setOpenedIndex] = useState(null)
  const [panelHeights, setPanelHeights] = useState({})
  const contentRefs = useRef([])

  const measureHeights = useCallback(() => {
    const nextHeights = {}

    items.forEach((_, index) => {
      const contentElement = contentRefs.current[index]
      nextHeights[index] = contentElement ? contentElement.scrollHeight : 0
    })

    setPanelHeights(nextHeights)
  }, [items])

  useLayoutEffect(() => {
    measureHeights()
  }, [measureHeights])

  useEffect(() => {
    contentRefs.current = contentRefs.current.slice(0, items.length)
  }, [items.length])

  useEffect(() => {
    let frameId = null

    const handleViewportChange = () => {
      if (frameId) {
        window.cancelAnimationFrame(frameId)
      }
      frameId = window.requestAnimationFrame(measureHeights)
    }

    window.addEventListener('resize', handleViewportChange)
    window.addEventListener('orientationchange', handleViewportChange)

    return () => {
      if (frameId) {
        window.cancelAnimationFrame(frameId)
      }
      window.removeEventListener('resize', handleViewportChange)
      window.removeEventListener('orientationchange', handleViewportChange)
    }
  }, [measureHeights])

  useEffect(() => {
    if (typeof ResizeObserver === 'undefined') {
      return undefined
    }

    const observer = new ResizeObserver(() => {
      measureHeights()
    })

    contentRefs.current.forEach((contentElement) => {
      if (contentElement) {
        observer.observe(contentElement)
      }
    })

    return () => {
      observer.disconnect()
    }
  }, [items, measureHeights])

  const handleToggle = (index) => {
    setOpenedIndex((prev) => (prev === index ? null : index))
  }

  if (!isLoading && items.length === 0) return null

  return (
    <section className={`question-block ${className}`.trim()} aria-label={ariaLabel} aria-busy={isLoading}>
      <div className="question-block__inner">
        <header className="question-block__heading">
          <h2 className="question-block__title">{title}</h2>
          <p className="question-block__description">
            {description.split('\n').map((line, index) => (
              <React.Fragment key={`${line}-${index}`}>
                {index > 0 ? <br /> : null}
                {line}
              </React.Fragment>
            ))}
          </p>
        </header>

        {isLoading ? (
          <ul className="question-block__list">
            {Array.from({ length: PLACEHOLDER_QUESTION_COUNT }).map((_, index) => (
              <li className="question-block__item" key={`question-skeleton-${index}`} aria-hidden="true">
                <div className="question-block__trigger">
                  <span className="question-block__question-group">
                    <span className="question-block__number-skeleton img-skeleton" />
                    <span className="question-block__question-skeleton img-skeleton" />
                  </span>
                </div>
              </li>
            ))}
          </ul>
        ) : (
        <ul className="question-block__list">
          {items.map((item, index) => {
            const isOpen = openedIndex === index
            const formattedNumber = String(index + 1).padStart(2, '0')

            return (
              <li
                className={`question-block__item ${isOpen ? 'is-open' : ''}`}
                key={`${item.question}-${index}`}
              >
                <button
                  type="button"
                  className="question-block__trigger"
                  onClick={() => handleToggle(index)}
                  aria-expanded={isOpen}
                  aria-controls={`question-answer-${index}`}
                >
                  <span className="question-block__question-group">
                    <span className="question-block__number" aria-hidden="true">
                      <span className="question-block__number-leading">{formattedNumber.charAt(0)}</span>
                      <span className="question-block__number-main">{formattedNumber.slice(1)}</span>
                    </span>
                    <span className="question-block__question">{item.question}</span>
                  </span>
                  <span className="question-block__icon" aria-hidden="true">
                    <span className={`question-block__icon-shape ${isOpen ? 'is-open' : ''}`} />
                  </span>
                </button>

                <div
                  id={`question-answer-${index}`}
                  className="question-block__panel"
                  style={{ maxHeight: isOpen ? `${panelHeights[index] || 0}px` : '0px' }}
                  aria-hidden={!isOpen}
                >
                  <div
                    className="question-block__panel-content"
                    ref={(element) => {
                      contentRefs.current[index] = element
                    }}
                  >
                    <p className="question-block__answer">{item.answer}</p>
                  </div>
                </div>
              </li>
            )
          })}
        </ul>
        )}
      </div>
    </section>
  )
}

export default QuestionBlock
